import { Alert, Box, Divider, InputLabel, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { useMemo, useState } from 'react';

import { Button } from '../../../../../components/buttons';
import { BasicCheckBox } from '../../../../../components/checkboxes';
import { DateRangePicker } from '../../../../../components/date';
import {
  ButtonDropDownMenu,
  Dropdown,
  MultipleSelectChip,
} from '../../../../../components/dropDowns';
import { Loading } from '../../../../../components/progress';
import { NumberInputField } from '../../../../../components/textFieilds';
import { TimeSelector } from '../../../../../components/timePicker';
import {
  INPUT_PARAM_MENU_CONFIG,
  INPUT_PARAM_MENU_CONFIG_FOR_DEFAULT,
} from '../../../../../configs/inputParamConfig';
import { useToast } from '../../../../../contexts';
import { useApiData } from '../../../../../hooks';
import useForm, {
  FormValidationRules,
  IuseForm,
} from '../../../../../hooks/useForm';
import { END_POINTS } from '../../../../../services';
import SotService, {
  ForcedShiftParam,
  InputVersionParam,
  UpdateInputParamVersion,
} from '../../../../../services/SotService';
import { SHIFT_LENGTHS, TEXT_CONTENT } from '../../../../../utils/constants';
import {
  DATE_FORMAT,
  dateDiffCheck,
  getUTCDateTime,
  isValidDate,
  toDisplayDate,
} from '../../../../../utils/date';
import { useDashboardContext } from '../../../contexts';
import { useFacilityContext } from '../../../contexts/FacilityProvider';
import {
  ShiftDataCollection,
  ShiftProviderState,
} from '../../../contexts/ShiftProvider.types';
import { ForcedShiftControllerModal } from '../shiftController';
import PctAppCases from './PctCaseVal';
import SaveAsPrompt from './SaveAsPrompt';
import { IUserParamChooserProps } from './UserParamChooser.types';

const arrivalCubeMaxDate = new Date();
// arrivalCubeMaxDate.setDate(arrivalCubeMaxDate.getDate() - 28);
arrivalCubeMaxDate.setMonth(arrivalCubeMaxDate.getMonth() - 1, 0);
// const arrivalCubeMinDate = new Date(arrivalCubeMaxDate.toISOString());
// arrivalCubeMinDate.setFullYear(arrivalCubeMinDate.getFullYear() - 4, 0, 1);

function UserParamChooser({
  onCancel,
  disabled = false,
  activeVersion = '',
  activeGraphParams = {},
  onSubmit,
}: {
  onCancel?: () => void;
  disabled?: boolean;
  activeVersion?: string;
  activeGraphParams?: any;
  onSubmit?: (v: any) => Promise<void>;
}) {
  const {
    state: { selectedFacility },
  } = useFacilityContext();

  const [prevActiveVersion, setPrevActiveVersion] = useState(activeVersion);
  const [currentParamVersion, setCurrentParamVersion] = useState(activeVersion);
  if (prevActiveVersion !== activeVersion) {
    setPrevActiveVersion(activeVersion);
    setCurrentParamVersion(activeVersion);
  }
  const {
    formData,
    setFieldValue,
    handleChange,
    isFormValid,
    errors,
    handleSubmit,
    reset,
  } = useForm(initialState, validationRule, (v) =>
    onSubmit?.({ ...v, versionId: currentParamVersion })
  );

  const {
    data: versionDfaultParamData = {},
    isLoading: isVersionDefaultParamLoading,
    reValidate: revalidateVersionDefaultParam,
  } = useApiData(END_POINTS.SOT.getUserParams(selectedFacility), {
    params: {
      version_id: currentParamVersion || undefined,
    },
  });
  const isParamChanged = useMemo(() => {
    if (
      activeVersion === currentParamVersion &&
      versionDfaultParamData &&
      activeGraphParams
    ) {
      return !isInputQueryParamsEqual(
        activeGraphParams,
        versionDfaultParamData
      );
    }
    return false;
  }, [
    activeGraphParams,
    activeVersion,
    currentParamVersion,
    versionDfaultParamData,
  ]);

  const [prevDefaultParamValues, setprevDefaultParamValues] = useState({});
  const [prevActiveGrapParams, setPrevActiveGraphParams] = useState({});

  const patchUserParam = (value: any) => {
    let newShiftCollection: ShiftDataCollection = {};

    value?.forced_shift?.forEach?.((element: any, index: any) => {
      const startDt = new Date(element?.DateHour);
      const shiftLength = element?.ShiftLength || 0;
      const endDt = new Date(element?.DateHour);
      endDt.setHours(endDt.getHours() + shiftLength);
      newShiftCollection[index + 1] = {
        id: index + 1,
        startDate: startDt.toISOString(),
        shiftLength: shiftLength,
        endDate: endDt.toISOString(),
        providerType: element?.ProviderTypeName,
        providerCount: 1,
        hours: shiftLength,
      };
    });

    let newUserParam = {
      enableOverrideValue: value?.patient_volume_flag ?? false,
      baselineEnableOverrideValue: value?.base_line_flag ?? false,
      patientVolume: value?.TotalCasesDailyAvgOverride ?? '',
      appCasesHrBaseline: value?.APPCasesPerHourBaseline ?? '',
      phyCasesHrBaseline: value?.PhyCasesPerHourBaseline ?? '',
      appShiftLength: value?.facility_app_shift_length || [],
      phyShiftLength: value?.facility_physician_shift_length || [],
      shiftStartTime: value?.shift_start_time || '',
      arrivalCubeData: ['', ''],
      pctCasesAppAuto: {
        '99281':
          value?.PctOfCases99281APPAuto !== '' &&
          !isNaN(value?.PctOfCases99281APPAuto * 100)
            ? Math.round(value?.PctOfCases99281APPAuto * 100)
            : '',
        '99282':
          value?.PctOfCases99282APPAuto !== '' &&
          !isNaN(value?.PctOfCases99282APPAuto * 100)
            ? Math.round(value?.PctOfCases99282APPAuto * 100)
            : '',
        '99283':
          value?.PctOfCases99283APPAuto !== '' &&
          !isNaN(value?.PctOfCases99283APPAuto * 100)
            ? Math.round(value?.PctOfCases99283APPAuto * 100)
            : '',
        '99284':
          value?.PctOfCases99284APPAuto !== '' &&
          !isNaN(value?.PctOfCases99284APPAuto * 100)
            ? Math.round(value?.PctOfCases99284APPAuto * 100)
            : '',
        '99285':
          value?.PctOfCases99285APPAuto !== '' &&
          !isNaN(value?.PctOfCases99285APPAuto * 100)
            ? Math.round(value?.PctOfCases99285APPAuto * 100)
            : '',
        '99291':
          value?.PctOfCases99291APPAuto !== '' &&
          !isNaN(value?.PctOfCases99291APPAuto * 100)
            ? Math.round(value?.PctOfCases99291APPAuto * 100)
            : '',
      },
      forcedShifts: newShiftCollection,
    };

    if (isValidDate(value?.StartDate) && isValidDate(value?.EndDate)) {
      newUserParam.arrivalCubeData = [
        new Date(value?.StartDate)?.toISOString(),
        new Date(value?.EndDate)?.toISOString(),
      ];
    }
    reset(newUserParam);
  };
  if (
    JSON.stringify(activeGraphParams) !== JSON.stringify(prevActiveGrapParams)
  ) {
    setPrevActiveGraphParams(activeGraphParams);
    patchUserParam(activeGraphParams);
  }

  if (
    JSON.stringify(versionDfaultParamData) !==
    JSON.stringify(prevDefaultParamValues)
  ) {
    setprevDefaultParamValues(versionDfaultParamData);

    if (activeVersion === currentParamVersion) {
      patchUserParam(activeGraphParams);
    } else patchUserParam(versionDfaultParamData);
  }

  const {
    state: {
      paramVersions: { data: paramVersionList },
    },
    reValidateParamVersionList,
  } = useDashboardContext();

  const { showToast } = useToast();

  const [isOpenAdvancedParams, setIsOpenAdvancedParams] =
    useState<boolean>(false);
  const [isOpenSaveAsPrompt, setIsOpenSaveAsPrompt] = useState(false);
  const shiftLengthOptions = useMemo(() => {
    return SHIFT_LENGTHS.map((d) => ({ id: d, name: `${d} Hours` }));
  }, []);

  let shiftStartTime = '';

  if (formData?.shiftStartTime !== '') {
    let dt = new Date();
    dt.setHours(formData?.shiftStartTime, 0, 0, 0);
    shiftStartTime = dt.toISOString();
  }
  const onForcedShiftUpdate = async (
    v: ShiftProviderState['shifts']['data']
  ) => {
    const changedShifts = Object.values(v);
    const newShifts = { ...v };

    changedShifts.forEach((d) => {
      if (d.status === 'delete') {
        delete newShifts?.[d.id];
      } else
        newShifts[d.id] = {
          ...newShifts[d.id],
          status: undefined,
        };
    });
    setFieldValue('forcedShifts', newShifts);

    setIsOpenAdvancedParams(false);
  };
  const handleMoreMenuItemClick = async (id: string) => {
    switch (id) {
      case 'save':
        try {
          let params: UpdateInputParamVersion = {
            ...getInputParamForSave(formData),
            version_id: currentParamVersion,
          };

          await SotService.updateParamVersion(selectedFacility, [params]);
          await revalidateVersionDefaultParam();
          showToast(TEXT_CONTENT.INPUT_PARAM_VERSION_UPDATED);
        } catch (error) {
          console.error('API Error 💥', error);

          showToast(TEXT_CONTENT.INPUT_PARAM_SAVE_FAILED, 'error');
        }

        break;
      case 'save-as':
        // showToast('saved as a new version');
        setIsOpenSaveAsPrompt(true);
        break;
      case 'delete':
        try {
          await SotService.deleteParamVersion(
            selectedFacility,
            currentParamVersion
          );
          await reValidateParamVersionList?.();
          setCurrentParamVersion?.('');
          // dashboardDispatch(dashboardActions.setSelectedParamVersion(''));
          showToast('Deleted the current input param version');
        } catch (error) {
          console.error('API Error 💥', error);

          showToast(TEXT_CONTENT.INPUT_PARAM_SAVE_FAILED, 'error');
        }
        break;
    }
  };
  const handleSaveAsVersion = async (v: string) => {
    try {
      const res = await SotService.addNewVersion(selectedFacility, [
        getInputParamForSave(formData, v),
      ]);
      if (res?.status === 200) {
        showToast(TEXT_CONTENT.NEW_INPUT_PARAM_VERSION_ADDED);
        await reValidateParamVersionList?.();
        setCurrentParamVersion(res?.data || '');
        // dashboardDispatch(
        //   dashboardActions.setSelectedParamVersion(res?.data || '')
        // );
      } else {
        showToast(TEXT_CONTENT.INPUT_PARAM_SAVE_FAILED, 'error');
      }
    } catch (error) {
      console.error('API Error 💥', error);

      showToast(TEXT_CONTENT.INPUT_PARAM_SAVE_FAILED, 'error');
    } finally {
      setIsOpenSaveAsPrompt(false);
    }
  };

  return (
    <>
      <ForcedShiftControllerModal
        open={isOpenAdvancedParams}
        onClose={() => {
          setIsOpenAdvancedParams(false);
        }}
        shiftData={formData?.forcedShifts || {}}
        onUpdate={onForcedShiftUpdate}
      />
      <SaveAsPrompt
        key={String(isOpenSaveAsPrompt)}
        isOpen={isOpenSaveAsPrompt || false}
        onClose={() => setIsOpenSaveAsPrompt(false)}
        onOk={handleSaveAsVersion}
      />

      <form
        name="user-param-form"
        style={{ height: '100%' }}
        onSubmit={handleSubmit}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            width: '31rem',
            height: '100%',
          }}
        >
          <Box sx={{ flex: '0 1 auto' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontSize: '1.12rem', fontWeight: 750 }}
              >
                Input Parameters
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  justifyContent: 'flex-end',
                  gap: 1,
                }}
              >
                <Dropdown
                  id="selectedParamVersion"
                  name="selectedParamVersion"
                  onChange={setCurrentParamVersion}
                  value={currentParamVersion}
                  options={paramVersionList || []}
                  sx={{
                    maxWidth: '10rem',
                    width: '100%',
                    height: '100%',
                  }}
                  fullWidth
                  size="small"
                  label={TEXT_CONTENT.VIEW_SAVED}
                />
                <ButtonDropDownMenu
                  onItemClick={handleMoreMenuItemClick}
                  options={
                    !currentParamVersion || currentParamVersion === ''
                      ? INPUT_PARAM_MENU_CONFIG_FOR_DEFAULT
                      : INPUT_PARAM_MENU_CONFIG
                  }
                />
              </Box>
            </Box>
            {isParamChanged && (
              <Alert
                severity="info"
                sx={{ p: 0, alignItems: 'center', px: '1rem', mt: 1 }}
              >
                You're viewing unsaved generated schedule input parameters.
                Please save to avoid losing changes.
              </Alert>
            )}
            <Divider sx={{ mx: -2, mt: 2 }} orientation="horizontal" flexItem />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              mx: -2,
              px: 2,
              pb: 2,
              minHeight: '10rem',
            }}
          >
            {isVersionDefaultParamLoading ? (
              <Loading />
            ) : (
              <fieldset
                disabled={disabled}
                style={{
                  border: 'none',
                  pointerEvents: disabled ? 'none' : 'all',
                }}
              >
                <StyledFormGroup>
                  <InputLabel htmlFor="patientVolume">
                    Patient Volume
                  </InputLabel>
                  <BasicCheckBox
                    size="medium"
                    name="enableOverrideValue"
                    onChange={handleChange}
                    checked={formData?.enableOverrideValue ?? false}
                    label="Enable Override Value"
                  />
                  <NumberInputField
                    disabled={!formData?.enableOverrideValue}
                    id="patientVolume"
                    name="patientVolume"
                    onChange={handleChange}
                    value={formData?.patientVolume ?? ''}
                    inputProps={{ min: 0 }}
                    required={formData?.enableOverrideValue}
                  />
                </StyledFormGroup>
                <Divider
                  sx={{ mx: -2, mt: 2 }}
                  orientation="horizontal"
                  flexItem
                />
                <StyledFormGroup>
                  <InputLabel htmlFor="arrivalCubeData">DOS Range</InputLabel>
                  <DateRangePicker
                    defaultValue={[]}
                    name="arrivalCubeData"
                    onChange={(val) => setFieldValue('arrivalCubeData', val)}
                    value={formData?.arrivalCubeData ?? ''}
                    maximumDate={arrivalCubeMaxDate.toISOString()}
                  />
                  <StyledFormError>
                    {errors?.arrivalCubeData || ''}
                  </StyledFormError>
                </StyledFormGroup>
                <Divider
                  sx={{ mx: -2, mt: 2 }}
                  orientation="horizontal"
                  flexItem
                />
                <StyledFormGroup>
                  <Typography>APP % Visits</Typography>
                  <StyledFormGroup>
                    <InputLabel>% of Cases APP Autonomous</InputLabel>
                    <PctAppCases
                      key="auto"
                      value={formData?.pctCasesAppAuto || {}}
                      onChange={(val) => {
                        setFieldValue('pctCasesAppAuto', {
                          ...formData?.pctCasesAppAuto,
                          ...val,
                        });
                      }}
                    />
                  </StyledFormGroup>
                </StyledFormGroup>
                <Divider
                  sx={{ mx: -2, mt: 2 }}
                  orientation="horizontal"
                  flexItem
                />
                <StyledFormGroup>
                  <Typography>Physician and APP Patients/Hour</Typography>
                  <BasicCheckBox
                    size="medium"
                    name="baselineEnableOverrideValue"
                    onChange={handleChange}
                    checked={formData?.baselineEnableOverrideValue ?? false}
                    label="Enable Override Value"
                  />
                  <StyledFormGroup>
                    <InputLabel htmlFor="phyCasesHrBaseline">
                      Physician Cases Per Hour Baseline
                    </InputLabel>
                    <NumberInputField
                      disabled={!formData?.baselineEnableOverrideValue}
                      id="phyCasesHrBaseline"
                      name="phyCasesHrBaseline"
                      onChange={handleChange}
                      value={formData?.phyCasesHrBaseline ?? ''}
                      inputProps={{ min: 0, step: 0.001 }}
                    />
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <InputLabel htmlFor="appCasesHrBaseline">
                      APP Cases Per Hour Baseline
                    </InputLabel>
                    <NumberInputField
                      disabled={!formData?.baselineEnableOverrideValue}
                      id="appCasesHrBaseline"
                      name="appCasesHrBaseline"
                      onChange={handleChange}
                      value={formData?.appCasesHrBaseline ?? ''}
                      inputProps={{ min: 0, step: 0.001 }}
                    />
                  </StyledFormGroup>
                </StyledFormGroup>
                <Divider
                  sx={{ mx: -2, mt: 2 }}
                  orientation="horizontal"
                  flexItem
                />
                <StyledFormGroup>
                  <Typography>{TEXT_CONTENT.SHIFT_LENGTHS}</Typography>
                  <StyledFormGroup>
                    <InputLabel>Physician Shift Length</InputLabel>
                    <MultipleSelectChip
                      id="phyShiftLength"
                      name="phyShiftLength"
                      value={formData?.phyShiftLength ?? ''}
                      onChange={(val) => {
                        setFieldValue('phyShiftLength', val);
                      }}
                      disabled={disabled}
                      options={shiftLengthOptions || []}
                    />
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <InputLabel htmlFor="appShiftLength">
                      APP Shift Length
                    </InputLabel>
                    <MultipleSelectChip
                      id="appShiftLength"
                      name="appShiftLength"
                      value={formData?.appShiftLength ?? ''}
                      onChange={(val) => {
                        setFieldValue('appShiftLength', val);
                      }}
                      disabled={disabled}
                      options={shiftLengthOptions || []}
                    />
                  </StyledFormGroup>
                </StyledFormGroup>
                <Divider
                  sx={{ mx: -2, mt: 2 }}
                  orientation="horizontal"
                  flexItem
                />
                <StyledFormGroup>
                  <Typography>Shift Start Time</Typography>
                  <StyledFormGroup>
                    <InputLabel htmlFor="shiftStartTime">
                      Sunday Start Hour Of Day
                    </InputLabel>
                    <TimeSelector
                      onChange={(val) => {
                        if (val && val !== '' && isValidDate(val)) {
                          setFieldValue(
                            'shiftStartTime',
                            new Date(val).getHours()
                          );
                        } else {
                          setFieldValue('shiftStartTime', '');
                        }
                      }}
                      value={shiftStartTime ?? ''}
                      name="shiftStartTime"
                    />
                  </StyledFormGroup>
                </StyledFormGroup>
                <Divider
                  sx={{ mx: -2, mt: 2 }}
                  orientation="horizontal"
                  flexItem
                />
                <StyledFormGroup>
                  <Typography>Forced Shift Time</Typography>
                  <StyledFormGroup>
                    <Button
                      variant="outlined"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsOpenAdvancedParams((prev) => !prev);
                      }}
                    >
                      {TEXT_CONTENT.VIEW_OR_EDIT_FORCED_SHIFT_TIMES}
                    </Button>
                  </StyledFormGroup>
                </StyledFormGroup>
              </fieldset>
            )}
          </Box>
          <Box sx={{ flex: '0 1 auto' }}>
            <Divider sx={{ mx: -2 }} orientation="horizontal" flexItem />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 2,
                mt: 2,
              }}
            >
              {/* <Box
                sx={{ justifySelf: 'flex-start', flex: 1, textAlign: 'left' }}
              >
                <Link
                  component="button"
                  sx={{
                    fontSize: '1.12rem',
                    fontWeight: 500,
                    justifySelf: 'flex-start',
                    flex: 1,
                    textAlign: 'left',
                  }}
                  variant="h6"
                  underline="hover"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsOpenAdvancedParams((prev) => !prev);
                  }}
                >
                  Manage Forced Shifts
                </Link>
              </Box> */}
              <Button type="reset" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" disabled={!isFormValid || disabled}>
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </form>
    </>
  );
}
export default UserParamChooser;

const StyledFormGroup = styled(Box)<IUserParamChooserProps>(() => ({
  display: 'flex',
  flexFlow: 'column',
  marginTop: '1rem',
  '& label': {
    fontSize: '1.12rem',
    fontWeight: 500,
    marginBottom: '0.5rem',
  },
}));
const StyledFormError = styled('span')`
  color: red;
  margintop: 3px;
`;

const getInputParamForSave = (formData: IuseForm['formData'], v?: string) => {
  const _99281 = Number(formData?.pctCasesAppAuto?.['99281'] || 0) / 100;
  const _99282 = Number(formData?.pctCasesAppAuto?.['99282'] || 0) / 100;
  const _99283 = Number(formData?.pctCasesAppAuto?.['99283'] || 0) / 100;
  const _99284 = Number(formData?.pctCasesAppAuto?.['99284'] || 0) / 100;
  const _99285 = Number(formData?.pctCasesAppAuto?.['99285'] || 0) / 100;
  const _99291 = Number(formData?.pctCasesAppAuto?.['99291'] || 0) / 100;

  const changedShiftsParams: ForcedShiftParam[] =
    Object.values(formData?.forcedShifts || {})?.map?.((d: any) => {
      return {
        provider_type: d.providerType,
        shift_length: d.shiftLength,
        shift_start: getUTCDateTime(d.startDate),
      };
    }) || [];
  let startDate = formData?.arrivalCubeData?.[0]
    ? toDisplayDate(formData?.arrivalCubeData?.[0], DATE_FORMAT.DATE_YYYY_MM_DD)
    : undefined;
  let endDate = formData?.arrivalCubeData?.[0]
    ? toDisplayDate(formData?.arrivalCubeData?.[1], DATE_FORMAT.DATE_YYYY_MM_DD)
    : undefined;
  const params: InputVersionParam = {
    version_name: v,
    patient_volume_flag: formData?.enableOverrideValue ?? false,
    base_line_flag: formData?.baselineEnableOverrideValue ?? false,
    app_patients_per_hour: formData?.appCasesHrBaseline || 0,
    physician_patients_per_hour: formData?.phyCasesHrBaseline || 0,
    cube_end_date: endDate || '',
    cube_start_date: startDate || '',
    facility_app_shift_length: formData?.appShiftLength || [],
    facility_physician_shift_length: formData?.phyShiftLength || [],
    forced_shift_data: changedShiftsParams || [],
    patient_volume: formData?.patientVolume || 0,
    PctOfCases99281APPAuto: _99281,
    PctOfCases99282APPAuto: _99282,
    PctOfCases99283APPAuto: _99283,
    PctOfCases99284APPAuto: _99284,
    PctOfCases99285APPAuto: _99285,
    PctOfCases99291APPAuto: _99291,
    PctOfCases99281Shared: Number((1 - _99281).toFixed(2)),
    PctOfCases99282Shared: Number((1 - _99282).toFixed(2)),
    PctOfCases99283Shared: Number((1 - _99283).toFixed(2)),
    PctOfCases99284Shared: Number((1 - _99284).toFixed(2)),
    PctOfCases99285Shared: Number((1 - _99285).toFixed(2)),
    PctOfCases99291Shared: Number((1 - _99291).toFixed(2)),
    shift_start_time: formData?.shiftStartTime || 0,
  };
  return params;
};

const initialState = {
  enableOverrideValue: false,
  baselineEnableOverrideValue: false,
  patientVolume: '',
  arrivalCubeData: ['', ''],
  appCasesHrBaseline: '',
  phyCasesHrBaseline: '',
  appShiftLength: [],
  phyShiftLength: [],
  shiftStartTime: '',
  pctCasesAppAuto: {
    '99281': '',
    '99282': '',
    '99283': '',
    '99284': '',
    '99285': '',
    '99291': '',
  },
  pctCasesAppShared: {
    '99281': '',
    '99282': '',
    '99283': '',
    '99284': '',
    '99285': '',
    '99291': '',
  },
  forcedShifts: {},
};

const validationRule: FormValidationRules = {
  arrivalCubeData: (value) => {
    if (
      Array.isArray(value) &&
      value.length > 1 &&
      value.some((d) => isValidDate(d))
    ) {
      if (!value.every((d) => isValidDate(d))) return 'Invalid Date Range';
      let newDateVal = value.map((d) => new Date(d)?.valueOf());
      const diffDays = Math.ceil(
        dateDiffCheck(
          new Date(newDateVal[1]).toISOString(),
          new Date(newDateVal[0]).toISOString()
        )
      );
      if (diffDays < 0) {
        return "From date can't go beyond To date";
      } else if (diffDays >= 0 && diffDays < 6) {
        return 'Date range must have atleast 7 days gap';
      }
      return undefined;
    }
    return undefined;
  },
};

const isInputQueryParamsEqual = (p1: any, p2: any, keys = paramProperties) => {
  let _p1: any = {};
  let _p2: any = {};
  keys.forEach((d) => {
    if (d === 'forced_shift') {
      _p1[d] =
        p1?.[d]?.map?.((x: any) => {
          return {
            FacilityCode: x?.FacilityCode,
            DateHour: x?.DateHour,
            ProviderTypeName: x?.ProviderTypeName,
            ShiftLength: x?.ShiftLength,
          };
        }) || [];
      _p2[d] =
        p2?.[d]?.map?.((x: any) => {
          return {
            FacilityCode: x?.FacilityCode,
            DateHour: x?.DateHour,
            ProviderTypeName: x?.ProviderTypeName,
            ShiftLength: x?.ShiftLength,
          };
        }) || [];
    } else {
      _p1[d] = String(p1?.[d]);
      _p2[d] = String(p2?.[d]);
    }
  });

  if (
    !(
      String(_p1['patient_volume_flag']) === 'true' &&
      String(_p2['patient_volume_flag']) === 'true'
    )
  ) {
    delete _p1?.['TotalCasesDailyAvgOverride'];
    delete _p2?.['TotalCasesDailyAvgOverride'];
  }
  if (
    !(
      String(_p1['base_line_flag']) === 'true' &&
      String(_p2['base_line_flag']) === 'true'
    )
  ) {
    ['APPCasesPerHourBaseline', 'PhyCasesPerHourBaseline'].forEach((e) => {
      delete _p1?.[e];
      delete _p2?.[e];
    });
  }

  return JSON.stringify(_p1) === JSON.stringify(_p2);
};

const paramProperties = [
  'StartDate',
  'EndDate',
  'base_line_flag',
  'APPCasesPerHourBaseline',
  'PhyCasesPerHourBaseline',
  'patient_volume_flag',
  'TotalCasesDailyAvgOverride',
  'PctOfCases99281APPAuto',
  'PctOfCases99282APPAuto',
  'PctOfCases99283APPAuto',
  'PctOfCases99284APPAuto',
  'PctOfCases99285APPAuto',
  'PctOfCases99291APPAuto',
  'facility_app_shift_length',
  'facility_physician_shift_length',
  'forced_shift',
  'shift_start_time',
];
